/**
 * Congelamento do contexto das tags {} dentro do próprio documento.
 *
 * Regra (uma só, para todos os caminhos de renderização):
 *
 *   status  < 100  → o editor logado regrava o snapshot a cada salvamento (dado fresco)
 *   status >= 100  → ninguém regrava; toda renderização lê do snapshot
 *
 * Por que existe: as tags dependiam de dados que vivem em outros serviços (pessoa na events-api,
 * compra na pos-api, vendedor na users-api). Isso trazia dois problemas.
 *
 * 1. O link público não tem sessão para buscar nada, então cada dado precisava de uma resolução
 *    própria no servidor — e o que ninguém lembrasse de resolver saía em branco (foi o caso do
 *    vendedor e ainda é o caso da conta/`bill`).
 * 2. Um documento concluído renderizava com o dado de *hoje*, não com o de quando foi emitido. Como
 *    o PDF assinado é gerado a partir dessa renderização, editar um cadastro depois da assinatura
 *    fazia a tela divergir do PDF que a pessoa assinou.
 *
 * O `templateSnapshot` já congelava o modelo pelo mesmo motivo; isto completa o padrão para o
 * contexto. `attendantName` na coluna do formulário é a mesma ideia, e não por acaso é o único dado
 * que nunca quebrou.
 */

import { currentCompanyOffsetMinutes } from '../utils/format-datetime'
import type { ContextSnapshot } from '../../../modules/toolsApi/forms-v2/form-v2-forms/form-v2-form'

// o tipo mora no model (modules/ não pode depender de components/); reexportado para quem já o
// importava daqui
export type { ContextSnapshot }

/** Status a partir do qual o documento é imutável (ACTIVE=100, SIGNING=105, SIGNED=110). */
export const FROZEN_FROM_STATUS = 100

/** Contexto no formato que o form-fill/print consomem e repassam ao formsReplaceTags. */
export interface FillContext {
    person?: any
    invoice?: any
    bill?: any
    attendant?: any
    user?: { userName: string } | null
    timezoneOffsetMinutes?: number | null
}

export function isDocumentFrozen(status: number | null | undefined): boolean {
    return (status ?? 0) >= FROZEN_FROM_STATUS
}

/**
 * Monta o snapshot a partir do que o editor já tem carregado — não busca nada.
 *
 * As entidades são congeladas no formato em que já estão, de propósito: assim o `formsReplaceTags`
 * não precisa saber que o snapshot existe, e a leitura funciona igual vindo de dado vivo ou do banco.
 *
 * `user` é a exceção à regra de regravar: `{usuario}` significa "quem criou", então preserva o valor
 * já gravado e só usa o usuário da sessão na primeira vez.
 */
export function buildContextSnapshot(
    current: { person?: any; invoice?: any; bill?: any; attendant?: any },
    previousSnapshot?: ContextSnapshot | null,
): ContextSnapshot {
    const sessionUserName = typeof window !== 'undefined' ? window.store?.user?.userName : ''
    return {
        person: current.person ?? null,
        invoice: current.invoice ?? null,
        bill: current.bill ?? null,
        attendant: current.attendant?.userName ? { userName: current.attendant.userName } : null,
        user: previousSnapshot?.user ?? (sessionUserName ? { userName: sessionUserName } : null),
        timezoneOffsetMinutes: currentCompanyOffsetMinutes() ?? previousSnapshot?.timezoneOffsetMinutes ?? null,
    }
}

/**
 * Contexto de renderização para quem tem sessão (tela de preenchimento e impressão local).
 *
 * Abaixo de 100 usa o dado vivo, que é o que o editor acabou de carregar. De 100 em diante lê do
 * snapshot, para a impressão da equipe não divergir do PDF assinado — a divergência por dentro seria
 * ainda mais difícil de perceber que a do link público.
 */
export function resolveFillContext(
    status: number | null | undefined,
    live: { person?: any; invoice?: any; bill?: any; attendant?: any },
    snapshot: ContextSnapshot | null | undefined,
): FillContext {
    if (isDocumentFrozen(status) && snapshot) return readContextSnapshot(snapshot)
    return {
        person: live.person ?? null,
        invoice: live.invoice ?? null,
        bill: live.bill ?? null,
        attendant: live.attendant?.userName ? { userName: live.attendant.userName } : null,
        // Mesmo editando, `{usuario}` é quem criou — não quem está com a tela aberta agora.
        user: snapshot?.user ?? null,
        timezoneOffsetMinutes: snapshot?.timezoneOffsetMinutes ?? currentCompanyOffsetMinutes(),
    }
}

/**
 * Contexto de renderização para os caminhos públicos (preenchimento por link, impressão pública e o
 * PDF gerado pela lambda em cima dela).
 *
 * Sem fallback para dado vivo de propósito: um fallback traria de volta a divergência com o PDF
 * assinado, só que intermitente — pior de diagnosticar que a ausência.
 */
export function readContextSnapshot(snapshot: ContextSnapshot | null | undefined): FillContext {
    return {
        person: snapshot?.person ?? null,
        invoice: snapshot?.invoice ?? null,
        bill: snapshot?.bill ?? null,
        attendant: snapshot?.attendant ?? null,
        user: snapshot?.user ?? null,
        timezoneOffsetMinutes: snapshot?.timezoneOffsetMinutes ?? null,
    }
}
